import React, {useContext, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';

// Comments
import {Typography} from '@nur-portal/ui-kit';

import {CommentsEventLogger} from '../../../../services/analytics/comments';
import {Button} from '../../../../website-ui/components/button';
import CommentAlert from '../../../../website-ui/components/comments/comment-alert/comment-alert';
import CommentCard from '../../../../website-ui/components/comments/comment-card/commentCard';
import {CommentForm} from '../../../../website-ui/components/comments/comment-form-area';
import Link from '../../../../website-ui/components/link';
import {USER_ROLES} from '../../../../website-ui/constants/user';
import {NetworkStatusContext} from '../../../../website-ui/contexts/network-status-provider';
import {useUser} from '../../../../website-ui/hooks/use-user';
import {articleMapper} from '../../../../website-ui/mappers/comments/article-mapper';
import {saveComment} from '../../../../website-ui/use-cases/comments/save-comment';
// Tools
import {useArticleModel} from '../../../../website-ui/utils/shared/useArticleModel';
// Reactions
import CommentReactions from '../../reactions/components/comment-reactions';

// Icons
import {ReplyIcon} from './reply-icon';

// Styles
import styles from './modal-comments.module.css';

const LAST_LEVEL_OF_COMMENT_TO_REPLY = 4;
const LIMIT_COMMENTS_CODE = 429;

const Comment = ({
  articleId,
  comment,
  className,
  level,
  isLast,
  hideReactionsBlock = false
}) => {
  const {t} = useTranslation();
  const [isShowForm, setIsShowForm] = useState(false);
  const [isCommentLoading, setCommentLoading] = useState(false);
  const [saveReplyError, setSaveReplyError] = useState('');
  const user = useUser();

  const handleClickReplyButton = () => {
    CommentsEventLogger.clickReplyButton(articleId);
    setIsShowForm(true);
  };
  const article = useArticleModel(articleId);
  const isOnline = useContext(NetworkStatusContext);

  const handleSubmitCommentForm = currentUser => commentText => {
    if (!isOnline) {
      return setSaveReplyError(t('articleComments.connectionError'));
    }

    setCommentLoading(true);

    saveComment(commentText, comment.id, articleMapper.toAPI(article), currentUser)
      .then(data => {
        if (!data.isSucces && data.tokenError) {
          return;
        }

        if (!data.isSucces) {
          setSaveReplyError(data?.code === LIMIT_COMMENTS_CODE
            ? t('articleComments.limitError')
            : t('articleComments.publishError'));
        } else {
          setSaveReplyError('');
          setIsShowForm(false);
        }
      }).finally(() => {
        setCommentLoading(false);
      });
  };
  const handleResetCommentForm = () => {
    setIsShowForm(false);
    if (saveReplyError.length) {
      setSaveReplyError('');
    }
  };

  const getCommentButtons = (
    <>
      <div className={styles.buttonsWrapper}>
        {(user.username && user.role === USER_ROLES.EXTENDED) && (
          <Button
            className={styles.button}
            variant='border'
            onClick={handleClickReplyButton}
            disabled={isShowForm}
          >
            <ReplyIcon />
            <Typography variant='title-14'>{t('articleComments.reply')}</Typography>
          </Button>
        )}
      </div>
      {
        isShowForm ?
          <div className={styles.commentFormWrapper}>
            <CommentForm
              onSubmit={handleSubmitCommentForm(user)}
              onReset={handleResetCommentForm}
              error={saveReplyError}
              articleId={articleId}
              isLoading={isCommentLoading}
              isLast={isLast}
            />
          </div> :
          null
      }
    </>
  );

  let commentControls;

  if (!comment.recentlyAdded) {
    if (level <= LAST_LEVEL_OF_COMMENT_TO_REPLY) {
      commentControls = getCommentButtons;
    }
  } else {
    commentControls = (
      <CommentAlert
        type='info'
        className={styles.commentAlert}
      >
        <Typography variant="text-14">
          <Trans
            i18nKey="articleComments.moderationText"
            components={{
              text: <Link className={styles.link} href="/comment-rules.html" />
            }}
          />
        </Typography>
      </CommentAlert>
    );
  }

  return (
    <CommentCard
      className={className}
      key={comment.id}
      text={comment.text}
      userName={comment.user.username}
      date={comment.createdAt}
      level={level}
    >
      {!hideReactionsBlock ?
        <>
          {!comment.recentlyAdded && (
            <CommentReactions
              articleId={articleId}
              commentId={comment.id}
              isFromPreview={false}
            />)}
          {user.isAuthenticated ? commentControls : null}
        </> :
        null
      }
    </CommentCard>
  );
};

Comment.propTypes = {
  className: PropTypes.string,
  comment: PropTypes.object.isRequired,
  level: PropTypes.number.isRequired,
  isLast: PropTypes.bool
};

Comment.defaultProps = {
  className: '',
  isLast: false
};

export default Comment;
